<template>
    <div class="padding-normal">
        <div class="normal-container">
            <h2 class="mb-4 text-lg lg:mb-10 lg:text-3xl">{{ title }}</h2>
            <p v-if="text" class="max-w-[500px] text-[13px] lg:mb-10 lg:text-base">
                {{ text }}
            </p>
        </div>
        <div class="w-full overflow-hidden">
            <div class="normal-container">
                <LazyUiSlider
                    :displayButtons="false"
                    :breakpoints="{
                        '(min-width: 0px)': {
                            slides: { perView: 'auto', spacing: 16 },
                        },
                    }">
                    <div
                        v-for="(manufacturer, index) in manufacturers"
                        :key="manufacturer.id"
                        class="max-w-[128px] min-w-[128px] rounded-sm lg:max-w-[226px] lg:min-w-[226px]"
                        :class="['keen-slider__slide number-slide' + index]">
                        <div class="group relative !h-[140px] lg:!h-[246px]">
                            <NuxtLink
                                :to="getRelativeUrl(brandUrls[manufacturer?.link?._ref] ?? '#')"
                                class="group relative block transition-all duration-300 ease-in-out">
                                <div class="relative !overflow-hidden">
                                    <NuxtImg
                                        v-if="manufacturer.image"
                                        class="h-[140px] object-cover grayscale filter transition-transform duration-300 ease-in-out group-hover:grayscale-0 lg:h-[245px]"
                                        :src="
                                            useImageUrl(manufacturer.image).format('webp').width(245).quality(65).url()
                                        "
                                        sizes="100vw sm:50vw md:245px"
                                        height="245"
                                        width="245"
                                        :alt="`Merk: ${manufacturer.image.alt}`"
                                        placeholder
                                        format="webp" />
                                </div>

                                <div class="pointer-events-none absolute inset-0 flex items-center justify-center">
                                    <p
                                        v-if="manufacturer.text"
                                        class="text-center text-base text-white uppercase lg:text-2xl">
                                        {{ manufacturer.text }}
                                    </p>
                                </div>
                            </NuxtLink>
                        </div>
                    </div>
                    <div
                        :class="'keen-slider__slide number-slide' + manufacturers.length"
                        class="max-w-[128px] min-w-[128px] rounded-sm lg:max-w-[226px] lg:min-w-[226px]">
                        <NuxtLink :to="getUrlById(linkText.pageReference._ref)" v-if="linkText" class="h-full">
                            <div
                                class="bg-green-650 flex h-full max-w-[128px] min-w-[128px] flex-col items-center justify-between p-4 text-white md:p-2 lg:max-w-[226px] lg:min-w-[226px]">
                                <div class="h-full">
                                    <div
                                        class="flex h-full flex-col items-center justify-center space-y-2 text-center text-black">
                                        <p class="text-[10px] leading-4 font-light tracking-wider 2xl:text-xs">
                                            {{ linkText.title }}
                                        </p>

                                        <p class="font-monument-extended text-xs font-semibold lg:text-xl">
                                            {{ linkText.text }}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </NuxtLink>
                    </div>
                </LazyUiSlider>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
    import { useKeenSlider } from 'keen-slider/vue';

    const props = defineProps({
        image: Object,
        title: String,
        text: String,
        manufacturers: Array,
        product: Object,
        linkText: Object,
    });

    const { getUrlById } = useCategorySeoUrl();

    const brandUrls = ref({});
    const linkSeoUrl = ref('');
    const manufacturerIds = props.manufacturers
        .filter(manufacturer => manufacturer.link && manufacturer.link._ref)
        .map(manufacturer => manufacturer.link._ref);

    const fetchBrandUrls = async () => {
        // Fetch URLs for all manufacturer IDs (assuming useBrandUrls is returning an object)
        const urls = await useBrandUrls(manufacturerIds);
        // Map the manufacturer IDs to their URLs
        brandUrls.value = Object.fromEntries(Object.entries(urls).map(([id, { url }]) => [id, url]));
    };

    const fetchLinkSeoUrl = async () => {
        if (props.linkText) {
            linkSeoUrl.value = getUrlById(props.linkText.pageReference._ref);
        }
    };

    onBeforeMount(() => {
        fetchBrandUrls();
        fetchLinkSeoUrl();
    });

    function getRelativeUrl(url: string): string {
        if (!url) return '/';
        try {
            const urlObj = new URL(url);
            return urlObj.pathname;
        } catch {
            return url.startsWith('/') ? url : `/${url}`;
        }
    }
</script>
